import {
    alpha,
    Box,
    Button,
    Chip,
    Divider,
    Drawer,
    Typography,
    Grid2,
} from "@mui/material";

import { Label } from "@callveriq/components/custom_components/Typography/Label";
import { CloneSvg, CloseSvg } from "@callveriq/components/svg";

import { IconButton } from "@mui/material";

import { Stack } from "@mui/material";
import {
    generateCleanNanoID,
    healGroupBlockInternalGraph,
    isDarkMode,
    isDefined,
} from "@callveriq/utils/helper/common.helper";
import { Fragment, useState } from "react";
import {
    AIConfigNodeType,
    CombinationNode,
    GroupNodeType,
    MomentBlockFormType,
    SingleResponseNode,
} from "../../types/bert";
import { useGetExistingBlocks } from "../hooks/useGetExistingBlocks";
import VirtualList from "@callveriq/components/custom_components/VirtualList/VirtualList";
import DeleteDialog from "@callveriq/components/custom_components/Dialog/DeleteDialog";
import InfoSvg from "@callveriq/components/svg/InfoSvg";
import SearchSvg from "@callveriq/components/svg/SearchSvg";
import { useReactFlowStateContextProvider } from "../hooks/useReactFlowStateContextProvider";
import { BertNodeKeys, singleNodeTypesMap } from "../constants";
import useNodeContext from "../hooks/useNodeContext";
import NodeStatePorvider from "../context/NodeStatePorvider";
import { SingleResponseBlockType } from "../context/NodeStateContext";
import { Edge } from "reactflow";
import useDebounce from "@callveriq/hooks/useDebounce";
import Skeleton from "@callveriq/components/custom_components/Skeleton";
import EmptyDataSvg from "@callveriq/components/svg/EmptyDataSvg";
import EmptyState from "@callveriq/components/custom_components/reuseable/EmptyState";
import { LoadingButton } from "@mui/lab";
import useParameterAiConfigContext from "../hooks/useParameterAiConfigContext";
import Attribute from "./Blocks/ViewBlocks/Attribute";
import Moment from "./Blocks/ViewBlocks/Moment";
import Parameter from "./Blocks/ViewBlocks/Parameter";
import PredefinedFunction from "./Blocks/ViewBlocks/PredefinedFunction";
import ValidateInfo from "./Blocks/ViewBlocks/ValidateInfo";

const BlockCard = ({
    data: nodeData,
}: {
    data: Pick<AIConfigNodeType, "data" | "type"> & {
        name?: string;
        data_type?: NonNullable<SingleResponseBlockType>;
    };
}) => {
    const { type: nodeType, data_type, name } = nodeData ?? {};
    const { type: blockType, metadata } = nodeData?.data ?? {};
    const [open, setOpen] = useState<boolean>(false);
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const { createSingleResponseNode } = useReactFlowStateContextProvider();
    const { sourceId, direction, onNodeSaveCallBack, blockParentId } =
        useNodeContext();

    const displayName =
        name ||
        (isDefined(metadata) && "name" in metadata ? metadata.name : "-");

    const handleClone = () => {
        if (
            nodeType === BertNodeKeys.singleBlockNode ||
            nodeType === BertNodeKeys.groupBlockNode ||
            nodeType === BertNodeKeys.combinationBlockNode
        ) {
            if (data_type === "moment") {
                createSingleResponseNode<SingleResponseNode<"moment">>({
                    data: {
                        ...(nodeData.data as SingleResponseNode<"moment">),
                        metadata: {
                            ...(nodeData.data.metadata as MomentBlockFormType<
                                string[]
                            >),
                            id: generateCleanNanoID(10),
                        },
                    },
                    sourceId,
                    direction,
                    blockParentId,
                });
            } else if (
                nodeType === BertNodeKeys.groupBlockNode &&
                nodeData.data.type === "group_block"
            ) {
                // Regenerate internal node ids (and remap their edges) so the
                // clone doesn't carry duplicate empty ids that reactflow would
                // later collapse, silently dropping moment blocks.
                const groupData = nodeData.data as GroupNodeType;
                const healed = healGroupBlockInternalGraph(
                    groupData.metadata.nodes,
                    groupData.metadata.edges
                );
                createSingleResponseNode({
                    data: {
                        ...groupData,
                        metadata: {
                            ...groupData.metadata,
                            nodes: healed.nodes,
                            edges: healed.edges,
                        },
                    },
                    sourceId,
                    direction,
                    blockParentId,
                });
            } else
                createSingleResponseNode({
                    data: nodeData.data,
                    sourceId,
                    direction,
                    blockParentId,
                });
            onNodeSaveCallBack();
        }
    };
    const getEdgeLabel = (edges: Edge[], source: string) => {
        return (
            edges.find((edge) => edge.source === source)?.data.operator ||
            undefined
        );
    };

    if (
        nodeType === BertNodeKeys.singleBlockNode ||
        nodeType === BertNodeKeys.groupBlockNode ||
        nodeType === BertNodeKeys.combinationBlockNode
    ) {
        return (
            <>
                <Grid2
                    container
                    sx={(theme) => ({
                        mb: 1,
                        p: 1.5,
                        borderRadius: "6px",
                        bgcolor: alpha(theme.palette.textColors[999], 0.1),
                        transition: "all 0.1s ease",
                        border: "1px solid",
                        borderColor: "transparent",
                        "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),

                            borderColor: theme.palette.primary.main,
                        },
                    })}
                    className="cursor-pointer"
                    onClick={() => {
                        setOpenDrawer(true);
                    }}
                >
                    <Grid2
                        size={{ xs: 6 }}
                        component={Label}
                        colorType="333"
                        isEllipses
                        hasTooltip
                        variant="medium"
                        fontWeight={600}
                    >
                        {displayName}
                    </Grid2>
                    {
                        <Grid2
                            size={{ xs: 4 }}
                            component={Typography}
                            variant="medium"
                        >
                            {nodeType === BertNodeKeys.groupBlockNode
                                ? "Group Block"
                                : nodeType === BertNodeKeys.combinationBlockNode
                                ? "Combination Block"
                                : singleNodeTypesMap[
                                      blockType as keyof typeof singleNodeTypesMap
                                  ]}
                        </Grid2>
                    }

                    <Grid2
                        size={{ xs: 2 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(true);
                        }}
                        className="flex justify-center cursor-pointer"
                    >
                        <CloneSvg />
                    </Grid2>
                </Grid2>
                <DeleteDialog
                    open={open}
                    setOpen={setOpen}
                    onDelete={handleClone}
                    title="Do you wish to clone this block?"
                    message={
                        <div className="flex flex-col items-center justify-center gap-2">
                            <Typography
                                fontWeight={600}
                                variant="large"
                                color="primary.main"
                            >
                                {name}
                            </Typography>
                            <Typography>
                                <Box
                                    component={"span"}
                                    sx={{
                                        color: "danger",
                                        transform: "scale(1.2)",
                                        mr: 1,
                                    }}
                                >
                                    <InfoSvg />
                                </Box>
                                A clone will get created and can be edited.
                            </Typography>
                        </div>
                    }
                    okText="Clone"
                    isDanger={false}
                />
                <Drawer
                    anchor="right"
                    open={openDrawer}
                    onClose={() => setOpenDrawer(false)}
                    sx={{
                        zIndex: (theme) => theme.zIndex.modal + 10,
                        "& .MuiBackdrop-root": {
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                        },
                    }}
                    PaperProps={{
                        sx: {
                            width: "536px",
                            "& .block--config--back__button": {
                                display: "none",
                            },
                        },
                    }}
                >
                    <Box className="flex-shrink-0">
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2.5}
                            className="flex-shrink-0"
                        >
                            <Label
                                colorType="333"
                                variant="extraLarge"
                                sx={{ fontWeight: 600 }}
                            >
                                {nodeType === BertNodeKeys.groupBlockNode
                                    ? (metadata as GroupNodeType["metadata"])
                                          .name
                                    : "Clone Block"}
                            </Label>
                            <IconButton onClick={() => setOpenDrawer(false)}>
                                <CloseSvg />
                            </IconButton>
                        </Stack>
                        <Divider className="flex-shrink-0" />
                    </Box>
                    <Box
                        className="h-full overflow-scroll"
                        sx={{
                            p: 2,
                            "& button": {
                                userSelect: "none",
                                pointerEvents: "none",
                            },
                            "& .block_close_button": {
                                display: "none",
                            },
                            "& .view_block_close_button": {
                                display: "none",
                            },
                        }}
                    >
                        {nodeType === BertNodeKeys.singleBlockNode ? (
                            <NodeStatePorvider
                                onNodeSaveCallBack={() => {}}
                                nodeDataToUpdate={
                                    (
                                        nodeData.data as SingleResponseNode<
                                            NonNullable<SingleResponseBlockType>
                                        >
                                    ).metadata
                                }
                            >
                                {blockType === "attribute" ? (
                                    <Attribute />
                                ) : (
                                    <></>
                                )}
                                {blockType === "moment" ? <Moment /> : <></>}
                                {blockType === "parameter" && <Parameter />}
                                {blockType === "function" && (
                                    <PredefinedFunction />
                                )}
                                {blockType === "validateInfo" && (
                                    <ValidateInfo />
                                )}
                            </NodeStatePorvider>
                        ) : nodeType === BertNodeKeys.groupBlockNode ? (
                            (metadata as GroupNodeType["metadata"]).nodes
                                .filter(isDefined)
                                .map((block) => {
                                    const edgeLabel =
                                        isDefined(metadata) &&
                                        "edges" in metadata
                                            ? getEdgeLabel(
                                                  (
                                                      metadata as GroupNodeType["metadata"]
                                                  )?.edges ?? [],
                                                  block.id
                                              )
                                            : null;
                                    return (
                                        <Fragment key={block.id}>
                                            <BlockCard data={block} />
                                            {edgeLabel && (
                                                <div className="ml-10 relative">
                                                    <div
                                                        className={
                                                            "self-center w-px h-[60px] border border-solid border-neutral-400 border-opacity-20  mt-[-8px]"
                                                        }
                                                    />

                                                    <Chip
                                                        sx={{
                                                            bgcolor:
                                                                "primary.main",
                                                            color: "white",
                                                            transform:
                                                                "translate(-50%, 50%)",
                                                            top: 0,
                                                        }}
                                                        className="uppercase absolute"
                                                        label={edgeLabel}
                                                    />
                                                </div>
                                            )}
                                        </Fragment>
                                    );
                                })
                        ) : nodeType === BertNodeKeys.combinationBlockNode ? (
                            <>
                                {(
                                    metadata as CombinationNode["metadata"]
                                )?.blocks
                                    ?.filter(
                                        (block) =>
                                            isDefined(block) &&
                                            block.is_selected
                                    )
                                    .map((block) => {
                                        return (
                                            <BlockCard
                                                key={block.id}
                                                data={{
                                                    ...block,

                                                    type: BertNodeKeys.singleBlockNode,
                                                }}
                                            />
                                        );
                                    })}
                            </>
                        ) : (
                            <></>
                        )}
                    </Box>
                    <Divider
                        className="flex-shrink-0"
                        orientation="horizontal"
                    />
                    <Box
                        sx={{
                            p: 2,
                        }}
                        className="flex-shrink-0 flex flex-row-reverse gap-2"
                    >
                        <LoadingButton
                            variant="global"
                            color="primary"
                            onClick={handleClone}
                        >
                            Clone
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setOpenDrawer(false)}
                        >
                            Back
                        </Button>
                    </Box>
                </Drawer>
            </>
        );
    }
};

export default function CloneBlockDrawerUi({
    onClose,
}: {
    onClose: () => void;
}) {
    const [query, setQuery] = useState<string>("");
    const debouncedQuery = useDebounce(query, 500);

    const {
        aiConfigState: { activeDefaultBlock },
    } = useParameterAiConfigContext();
    const { isGroupBlockTree } = useReactFlowStateContextProvider();
    const {
        data: existingBlocksData,
        hasNext,
        fetchNext,
        isLoading,
        isFetching,
        isFetchingNext,
    } = useGetExistingBlocks(
        {
            block_name: debouncedQuery,
            type:
                isGroupBlockTree || isDefined(activeDefaultBlock?.blockId)
                    ? BertNodeKeys.singleBlockNode
                    : undefined,
        },
        false
    );

    return (
        <div className="flex flex-col h-full w-[536px]">
            <Box className="flex-shrink-0">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    p={2.5}
                    className="flex-shrink-0"
                >
                    <Label
                        colorType="333"
                        variant="extraLarge"
                        sx={{ fontWeight: 600 }}
                    >
                        Clone Block
                    </Label>
                    <IconButton onClick={onClose}>
                        <CloseSvg />
                    </IconButton>
                </Stack>
                <Divider className="flex-shrink-0" />
            </Box>
            <Stack
                className="flex-1 flex flex-col overflow-hidden"
                px={2.5}
                gap={1.5}
            >
                <Box
                    sx={(theme) => ({
                        background: "rgba(153, 153, 153, 0.10)",
                        p: 1,
                        borderRadius: "6px",
                        ...(!isDarkMode(theme) && {
                            border: "1px solid rgba(153, 153, 153, 0.10)",
                        }),
                        mt: 1.5,
                    })}
                    className="flex flex-shrink-0"
                >
                    <SearchSvg sx={{ mr: 1, flex: "0 1 auto" }} />
                    <Box
                        component="input"
                        placeholder="Search blocks"
                        sx={{
                            outline: "none",
                            background: "transparent",
                            fontSize: 12,
                            ml: 1,
                        }}
                        onChange={(e) => {
                            setQuery(e.target.value);
                        }}
                        className="flex-auto"
                    ></Box>
                </Box>

                <Box
                    className="flex-1 flex flex-col overflow-hidden"
                    sx={{
                        background: "rgba(153, 153, 153, 0.10)",
                        p: 2.5,
                        borderRadius: "6px",
                    }}
                >
                    <Grid2
                        container
                        sx={{ mb: 1, px: 1.5, borderRadius: "6px" }}
                    >
                        <Grid2
                            size={{ xs: 6 }}
                            component={Typography}
                            variant="small"
                        >
                            Block Name
                        </Grid2>

                        <Grid2
                            size={{ xs: 4 }}
                            component={Typography}
                            variant="small"
                        >
                            Block Type{" "}
                        </Grid2>

                        <Grid2
                            size={{ xs: 2 }}
                            component={Typography}
                            variant="small"
                            className="text-center"
                        >
                            Action{" "}
                        </Grid2>
                    </Grid2>
                    {(!isFetchingNext && isFetching) || isLoading ? (
                        <Skeleton rows={5} />
                    ) : existingBlocksData?.nodes?.length ? (
                        <VirtualList<AIConfigNodeType>
                            Component={BlockCard}
                            data={existingBlocksData?.nodes?.filter(isDefined)}
                            hasNext={hasNext}
                            fetchNext={fetchNext}
                            isLoading={isLoading}
                            isFetching={isFetching}
                            sx={{
                                p: 0,
                            }}
                            className="flex-1"
                        />
                    ) : (
                        <EmptyState
                            Component={<EmptyDataSvg />}
                            text="No Blocks Found!"
                            subtext="It looks like there are no blocks available right now.Please change the filters as no blocks found from the applied filters."
                        />
                    )}
                </Box>
            </Stack>
        </div>
    );
}
